import { useAtomValue } from 'jotai';
import { selectAtom } from 'jotai/utils';
import { useEffect, useRef } from 'react';

import { useMutatePatchDeviceFirmware } from '@/app-shell/chronicle/hooks/mutation/useMutatePatchDeviceFirmware';
import { useQueryListHardwareDevices } from '@/app-shell/chronicle/hooks/query/useQueryListHardwareDevices';
import { chronicleLogger as logger } from '@/app-shell/chronicle/utils/chronicle-logger';
import {
  CONNECTION_STATUS,
  chronicleConnectionContextAtom,
  chronicleDeviceStateAtom,
} from '@/atoms/chronicle';

/** Only the fields the reconcile gate depends on — avoids re-runs on unrelated device-state changes. */
const reconcileFieldsAtom = selectAtom(chronicleDeviceStateAtom, (state) => ({
  connectionStatus: state.connectionStatus,
  firmwareVersion: state.firmwareVersion,
  firmwareUpdateInProgress: state.firmwareUpdateInProgress,
}));

const linkedDeviceIdAtom = selectAtom(
  chronicleConnectionContextAtom,
  (ctx) => ctx.linkedDeviceId
);

/**
 * Connect-time backend firmware reconcile.
 *
 * The backend `hardware_device` record is only PATCHed by the post-OTA gate
 * (`useSetupChronicleOtaEvents`), so any firmware change we didn't drive — the
 * device updated out-of-band, or an in-app OTA whose PATCH never landed —
 * leaves the backend record stale. The Remote detail page then shows that
 * stale value on every cold load / refresh, before BLE reconnects and reports
 * the real version (APP-8399).
 *
 * When connected with a live BLE firmware that differs from a loaded backend
 * record, PATCH the backend so the persisted value self-heals. The BLE value
 * (`MCU&FW&`, applied by `mapDeviceState`) is ground truth. Deduped per
 * (device, version) so a stable connection never re-PATCHes. We skip while an
 * OTA is in progress; note the post-OTA gate may still PATCH the just-booted
 * version on reconnect — both writes are idempotent and independently deduped.
 */
export function useReconcileDeviceFirmware() {
  const { connectionStatus, firmwareVersion, firmwareUpdateInProgress } =
    useAtomValue(reconcileFieldsAtom);
  const linkedDeviceId = useAtomValue(linkedDeviceIdAtom);
  // Read-only cache access (enabled=false) — the restore hooks own fetching;
  // we just compare against whatever backend record is cached.
  const { data: devices } = useQueryListHardwareDevices(false);
  const { mutate: patchFirmwareMutate } = useMutatePatchDeviceFirmware();

  // Single PATCH per (device, version) — a stable connection re-renders often.
  const lastReconciledKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (connectionStatus !== CONNECTION_STATUS.CONNECTED) return;
    // Skip while an OTA is running — the post-OTA gate drives that write.
    if (firmwareUpdateInProgress) return;
    if (!linkedDeviceId) return;
    if (!firmwareVersion) return;

    // Only reconcile against a loaded record. If the list hasn't been fetched
    // we can't know the record is stale, and a freshly-linked device already
    // carries the correct firmware from its link POST — so an unknown cache
    // must not trigger a blind write.
    const record = devices?.find((device) => device.id === linkedDeviceId);
    if (!record) return;
    const backendFirmware = record.firmware_version ?? null;
    // Already in sync — nothing to heal.
    if (backendFirmware === firmwareVersion) return;

    const key = `${linkedDeviceId}:${firmwareVersion}`;
    if (lastReconciledKeyRef.current === key) return;
    lastReconciledKeyRef.current = key;

    logger.info({
      msg: 'Reconciling stale backend firmware with live BLE version',
      linkedDeviceId,
      backendFirmware,
      firmwareVersion,
    });
    patchFirmwareMutate({ id: linkedDeviceId, firmwareVersion });
  }, [
    connectionStatus,
    firmwareVersion,
    firmwareUpdateInProgress,
    linkedDeviceId,
    devices,
    patchFirmwareMutate,
  ]);
}
